import { FC } from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { JobPerformanceKpi } from "@app/operations/job-performance/jobPerformanceInterfaces";
import { DATE_RANGE_OPTIONS, DateRangeKey, ProjectType } from "@app/operations/job-performance/JobPerformanceFilters";

interface JobPerformanceKpiCardsProps {
    clientName: string;
    dateRangeKey: DateRangeKey;
    kpis: JobPerformanceKpi | null;
    loading: boolean;
    projectType: ProjectType;
}

function getDateRangeLabel(dateRangeKey: DateRangeKey): string {
    return DATE_RANGE_OPTIONS.find(opt => opt.key === dateRangeKey)?.label ?? '';
}

const JOB_TYPE_1002_DEFAULT_TOOLTIP = 'Defaults to JobType 1002 (CustomAdequacyRegionSpecialtyGroupAccess) when no specific Job Type filter is selected.';
const TIME_TO_PUBLISH_TOOLTIP_COUNTY = 'Time from project run finishing to the publish button being clicked. Measures the manual response time — this approaches zero with self-service publishing.';
const QUEUE_WAIT_TOOLTIP_ZIP = 'Spans every zip pipeline job type that ran for a given Project Run Configuration — not just one job type.';
const RUN_TIME_TOOLTIP_ZIP = 'Measured MIN(DateQueued) to MAX(DateFinished) across all zip job types for the run — project-run-config, region, zip-class-aggregate, finalizer, plus NCP / Provider Changes when applicable.';
const TIME_TO_PUBLISH_TOOLTIP_ZIP = 'Time from run finishing to ProjectRunConfiguration.DatePublished being set. Zip projects publish synchronously — there is no background publish job.';
const END_TO_END_TOOLTIP_ZIP = 'Queue Wait + Project Run Time. Time to Publish is shown separately since publishing is a manual, later action, not part of the automated pipeline.';

const JobPerformanceKpiCards: FC<JobPerformanceKpiCardsProps> = ({ clientName, dateRangeKey, kpis, loading, projectType }) => {
    const subLabel = `${clientName} • ${getDateRangeLabel(dateRangeKey)}`;
    const showSpinner = loading || !kpis;

    const cards = projectType === 'Zip'
        ? [
            { label: 'Avg Queue Wait (Min)',          testId: 'job-performance__kpi-queue-wait',         tooltip: QUEUE_WAIT_TOOLTIP_ZIP,       value: kpis?.avgQueueWaitMinutes.toFixed(1) },
            { label: 'Avg Project Run Time (Min)',     testId: 'job-performance__kpi-run-time',           tooltip: RUN_TIME_TOOLTIP_ZIP,         value: kpis?.avgProjectRunTimeMinutes.toFixed(1) },
            { label: 'Avg Time to Publish (Min)',      testId: 'job-performance__kpi-time-to-publish',    tooltip: TIME_TO_PUBLISH_TOOLTIP_ZIP,  value: kpis?.avgTimeToPublishMinutes.toFixed(1) },
            { label: 'Avg Total End-to-End (Min)',     testId: 'job-performance__kpi-end-to-end',         tooltip: END_TO_END_TOOLTIP_ZIP,       value: kpis?.avgTotalEndToEndMinutes.toFixed(1) },
        ]
        : [
            { label: 'Avg Queue Wait (Min)',          testId: 'job-performance__kpi-queue-wait',         tooltip: JOB_TYPE_1002_DEFAULT_TOOLTIP,   value: kpis?.avgQueueWaitMinutes.toFixed(1) },
            { label: 'Avg Project Run Time (Min)',     testId: 'job-performance__kpi-run-time',           tooltip: JOB_TYPE_1002_DEFAULT_TOOLTIP,   value: kpis?.avgProjectRunTimeMinutes.toFixed(1) },
            { label: 'Avg Time to Publish (Min)',      testId: 'job-performance__kpi-time-to-publish',    tooltip: TIME_TO_PUBLISH_TOOLTIP_COUNTY,  value: kpis?.avgTimeToPublishMinutes.toFixed(1) },
            { label: 'Avg Publish Queue Wait (Min)',   testId: 'job-performance__kpi-pub-queue-wait',     tooltip: null,                             value: kpis?.avgPublishQueueWaitMinutes.toFixed(1) },
            { label: 'Avg Publish Run Time (Min)',     testId: 'job-performance__kpi-pub-run-time',       tooltip: null,                             value: kpis?.avgPublishRunTimeMinutes.toFixed(1) },
            { label: 'Avg Total End-to-End (Min)',     testId: 'job-performance__kpi-end-to-end',         tooltip: null,                             value: kpis?.avgTotalEndToEndMinutes.toFixed(1) },
        ];

    return (
        <div className={`jp-kpi-grid jp-kpi-grid--${projectType === 'Zip' ? 'zip' : 'county'}`}>
            {cards.map(card => (
                <div className="jp-kpi-card" data-testid={card.testId} key={card.testId}>
                    <div className="jp-kpi-card__header">
                        {card.label}
                        {card.tooltip && (
                            <span
                                className="jp-section-header__info"
                                data-tooltip={card.tooltip}
                            >
                                ⓘ
                            </span>
                        )}
                    </div>
                    <div className="jp-kpi-card__value">
                        {showSpinner ? <LoadingSpinner /> : card.value}
                    </div>
                    <div className="jp-kpi-card__sublabel">{subLabel}</div>
                </div>
            ))}
        </div>
    );
};

export default JobPerformanceKpiCards;
